import React, { useEffect, useRef, useState } from "react";
import { getCart } from "../api/cart";
import { checkout } from "../api/order";
import { getPaymentConfig } from "../api/payment";
import { getNearbyStores } from "../api/store";
import { useAuth } from "../context/AuthContext";
import { getGuestToken } from "../utils/guestToken";

const ORDER_TYPES = [
  { value: "DELIVERY", label: "배송" },
  { value: "PICKUP", label: "픽업" },
  { value: "DINE_IN", label: "매장이용" },
];

function formatPrice(v) {
  return Number(v || 0).toLocaleString() + "원";
}

/** 매장이용/픽업 선택 시 내 위치 기준 반경 1km 매장을 찾아 고를 수 있게 해주는 패널. */
function NearbyStorePicker({ storeId, onSelectStore }) {
  const [stores, setStores] = useState([]);
  const [locating, setLocating] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  const findNearbyStores = () => {
    if (!navigator.geolocation) {
      setError("이 브라우저는 위치 조회를 지원하지 않습니다.");
      return;
    }
    setError("");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await getNearbyStores(pos.coords.latitude, pos.coords.longitude, 1);
          setStores(res.data || []);
          setSearched(true);
        } catch (err) {
          setError(err.response?.data?.message || "매장을 조회하지 못했습니다.");
        } finally {
          setLocating(false);
        }
      },
      () => {
        setError("위치 접근이 거부되었습니다. 브라우저 위치 권한을 허용해주세요.");
        setLocating(false);
      }
    );
  };

  return (
    <div className="nearby-store-picker">
      <button type="button" className="btn-secondary-outline" onClick={findNearbyStores} disabled={locating}>
        {locating ? "내 위치 확인 중..." : "내 위치로 주변 매장 찾기 (반경 1km)"}
      </button>

      {error && <p className="error" style={{ marginTop: 8 }}>{error}</p>}

      {searched && stores.length === 0 && !error && (
        <p className="text-sub" style={{ marginTop: 8 }}>반경 1km 이내에 이용 가능한 매장이 없습니다.</p>
      )}

      {stores.length > 0 && (
        <div className="nearby-store-list">
          {stores.map((s) => (
            <label className={`nearby-store-item${storeId === s.storeId ? " selected" : ""}`} key={s.storeId}>
              <input type="radio" name="nearbyStore" checked={storeId === s.storeId} onChange={() => onSelectStore(s)} />
              <div className="nearby-store-info">
                <div className="nearby-store-name">{s.storeName}</div>
                <div className="text-sub">{s.address}</div>
              </div>
              <div className="nearby-store-distance">{s.distanceKm.toFixed(2)}km</div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CheckoutPage() {
  const { auth } = useAuth();

  const [cart, setCart] = useState(null);
  const [form, setForm] = useState({ receiverName: "", receiverPhone: "", receiverAddress: "", guestName: "", guestPhone: "" });
  const [orderType, setOrderType] = useState("DELIVERY");
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // state(submitting)는 리렌더링 이후에야 버튼이 disabled 되므로, 결제창이 뜨는 사이
  // 빠르게 다시 클릭(더블클릭)하면 체크아웃 API가 중복 호출되어 두 번째 요청이 이미 비워진
  // 장바구니를 보고 "장바구니가 비어 있습니다" 오류를 내는 문제가 있었다. ref는 리렌더링과
  // 무관하게 즉시(동기적으로) 반영되므로 이 틈을 막기 위한 별도 가드로 사용한다.
  const submittingRef = useRef(false);

  useEffect(() => {
    getCart(auth?.accessToken)
      .then((res) => setCart(res.data))
      .catch((err) => setError(err.response?.data?.message || "장바구니를 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [auth]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const changeOrderType = (value) => {
    setOrderType(value);
    setSelectedStore(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return; // 이미 처리 중인 제출이 있으면 중복 클릭을 무시한다.
    setError("");

    const needsStore = orderType === "PICKUP" || orderType === "DINE_IN";
    if (needsStore && !selectedStore) {
      setError("이용할 매장을 먼저 선택해주세요.");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const checkoutRes = await checkout(
        {
          receiverName: form.receiverName.trim(),
          receiverPhone: form.receiverPhone.trim(),
          receiverAddress: needsStore ? undefined : form.receiverAddress.trim(),
          orderType,
          storeId: needsStore ? selectedStore.storeId : undefined,
          guestName: auth ? undefined : form.guestName.trim(),
          guestPhone: auth ? undefined : form.guestPhone.trim(),
        },
        auth?.accessToken
      );
      if (!checkoutRes.success) {
        setError(checkoutRes.message || "주문 생성에 실패했습니다.");
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }
      const { orderGroupId, pgOrderId, amount, orderName } = checkoutRes.data;

      // 비회원 주문은 결제 완료 후(successUrl) 본인확인용 연락처가 필요하므로 세션에 잠시 보관한다.
      window.sessionStorage.setItem("onetak_last_order_group_id", String(orderGroupId));
      if (!auth) {
        window.sessionStorage.setItem("onetak_last_guest_phone", form.guestPhone.trim());
      }

      const configRes = await getPaymentConfig();
      const clientKey = configRes.data.clientKey;
      if (!window.TossPayments) {
        throw new Error("결제 모듈을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.");
      }
      const tossPayments = window.TossPayments(clientKey);
      const customerKey = auth ? `member-${auth.memberAccount}` : getGuestToken();
      const payment = tossPayments.payment({ customerKey });

      await payment.requestPayment({
        method: "CARD",
        amount: { currency: "KRW", value: amount },
        orderId: pgOrderId,
        orderName,
        customerName: form.receiverName.trim(),
        customerMobilePhone: form.receiverPhone.trim().replace(/-/g, ""),
        successUrl: `${window.location.origin}/checkout/success`,
        failUrl: `${window.location.origin}/checkout/fail`,
      });
      // 정상적으로는 여기 도달하지 않고 브라우저가 successUrl/failUrl 로 이동한다.
    } catch (err) {
      setError(err?.message || err.response?.data?.message || "결제 요청 중 오류가 발생했습니다.");
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page"><p className="text-sub">불러오는 중...</p></div>;

  const items = (cart?.items || []).filter((i) => !i.unavailable);
  if (!loading && items.length === 0) {
    return (
      <div className="page">
        <p className="error">결제할 수 있는 상품이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="page checkout-page">
      <h1 className="page-title">주문/결제</h1>

      <div className="checkout-summary">
        {items.map((item) => (
          <div className="checkout-summary-row" key={item.cartItemId}>
            <span>{item.productName} ({item.optionName}) x {item.qty}</span>
            <span>{formatPrice(item.lineTotal)}</span>
          </div>
        ))}
        {cart.orderDiscountAmount > 0 && (
          <>
            <div className="checkout-summary-row text-sub">
              <span>상품금액</span>
              <span>{formatPrice(cart.totalAmount)}</span>
            </div>
            <div className="checkout-summary-row text-sub">
              <span>주문할인{cart.orderDiscountName ? ` (${cart.orderDiscountName})` : ""}</span>
              <span>-{formatPrice(cart.orderDiscountAmount)}</span>
            </div>
          </>
        )}
        <div className="checkout-summary-row checkout-summary-total">
          <span>총 결제금액</span>
          <span>{formatPrice(cart.orderDiscountAmount > 0 ? cart.finalAmount : cart.totalAmount)}</span>
        </div>
        {(orderType === "PICKUP" || orderType === "DINE_IN") && (
          <p className="text-sub" style={{ fontSize: "0.85em" }}>
            * 이용 매장 전용 할인이 있는 경우, 실제 결제금액은 매장 선택 후 결제 시점에 다시
            계산되어 위 예상 금액과 다를 수 있습니다.
          </p>
        )}
      </div>

      <form className="reservation-form" onSubmit={handleSubmit}>
        <label>수령방법</label>
        <div className="order-type-tabs">
          {ORDER_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`order-type-tab${orderType === t.value ? " active" : ""}`}
              onClick={() => changeOrderType(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <label>수령인명</label>
        <input value={form.receiverName} onChange={set("receiverName")} required />

        <label>수령인 연락처</label>
        <input value={form.receiverPhone} onChange={set("receiverPhone")} placeholder="010-0000-0000" required />

        {orderType === "DELIVERY" ? (
          <>
            <label>배송지 주소</label>
            <input value={form.receiverAddress} onChange={set("receiverAddress")} required />
          </>
        ) : (
          <>
            <label>{orderType === "PICKUP" ? "픽업 매장" : "이용 매장"}</label>
            {selectedStore ? (
              <div className="selected-store-box">
                <div>
                  <strong>{selectedStore.storeName}</strong>
                  <div className="text-sub">{selectedStore.address} · {selectedStore.distanceKm.toFixed(2)}km</div>
                </div>
                <button type="button" className="link-btn" onClick={() => setSelectedStore(null)}>다시 선택</button>
              </div>
            ) : (
              <NearbyStorePicker storeId={selectedStore?.storeId} onSelectStore={setSelectedStore} />
            )}
          </>
        )}

        {!auth && (
          <>
            <label>주문자명 (비회원)</label>
            <input value={form.guestName} onChange={set("guestName")} required />

            <label>주문자 연락처 (비회원)</label>
            <input value={form.guestPhone} onChange={set("guestPhone")} placeholder="010-0000-0000" required />
          </>
        )}

        {error && <p className="error">{error}</p>}

        <button type="submit" disabled={submitting}>
          {submitting ? "결제창 여는 중..." : `${formatPrice(cart.orderDiscountAmount > 0 ? cart.finalAmount : cart.totalAmount)} 결제하기`}
        </button>
      </form>
    </div>
  );
}
